import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useForm,
} from 'react-hook-form';
import clsx from 'clsx';
import {
  CommentActivity,
} from 'types/toDo';
import Controller from 'core/components/FormController';
import VirtualList from 'core/components/VirtualList';
import {
  useCommentOnToDo,
  useToDoComments,
} from 'pages/Dashboard/pages/Todos/hooks';
import Button from 'pages/Dashboard/components/Button';
import EmptyState from 'pages/Dashboard/pages/Todos/EmptyState';
import useSignalREffect from 'views/EMR/hooks/signalR';
import TextArea from 'core/components/TextArea';
import Image from 'pages/Dashboard/components/Image';
import DOMPurify from 'dompurify';
import Name from 'pages/Dashboard/components/Name';
import {
  formatDateTime,
} from 'utils/date';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useToDoMentions,
} from 'pages/Dashboard/pages/Todos/mentions';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import compact from 'lodash/compact';
import {
  useMedia,
} from 'core/hooks';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';

type Props = {
  toDoId: number;
  isAbandoned?: boolean;
  hasComments?: boolean;
};

type RowProps = {
  data: CommentActivity;
};

type FormProps = {
  toDoId: number;
};

type ToDoCommentData = {
  comment: string;
};

function CommentRow({ data }: RowProps) {
  const rowClasses = 'flex flex-col w-full pb-4';
  const displayName = getDisplayName(data?.creator?.formattedNames);
  const src = useUserProfilePicturePath(data?.creator?.id);

  return (
    <div className={rowClasses}>
      <div className="flex flex-row items-start gap-2">
        <Image
          size="sm"
          className="font-medium"
          alt={displayName}
          src={src}
        >
          {data?.creator?.formattedNames?.initials}
        </Image>
        <div className="flex flex-col">
          <div className="flex flex-row items-center text-sm leading-snug">
            <Name
              className="!w-fit"
              labelClassName="!font-semibold text-gray-900 text-sm"
              name={displayName}
            />
            <span className="text-xs text-gray-400 ml-1">•</span>
            <span className="text-xs text-gray-400 ml-1">
              {formatDateTime(data.createdOn)}
            </span>
          </div>
          <div className="!text-xs !text-gray-700 mt-0.5 w-[540px] [word-break:break-word]">
            <div
              className="h-full overflow-y-auto ck-content"
              // oxlint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.comment) }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentForm(props: FormProps) {
  const [clearContent, setClearContent] = useState<boolean>(false);
  const { toDoId } = props;
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<ToDoCommentData>();

  const {
    mutateAsync: commentOnToDo,
    isPending: isCommenting,
  } = useCommentOnToDo();
  const {
    mentionsConfig,
    handleEditorChange,
    resetTaggedUserIds,
    getMergedTaggedUserIds,
    taggedUserIds,
  } = useToDoMentions();

  const onSubmit = async (data: ToDoCommentData) => {
    const { comment } = data;
    await commentOnToDo({ toDoId, comment, taggedUserIds: getMergedTaggedUserIds() });
    if(taggedUserIds?.length > 0) {
      toDosEventChannel.emit('onToDosChanged', toDoId);
    }
    reset();
    resetTaggedUserIds();
    setClearContent(true);
    setTimeout(() => { setClearContent(false); }, 0);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
      <div className="relative px-6">
        <Controller
          name="comment"
          control={control}
          rules={{ required: true }}
          render={
          ({ field }) => (
            <TextArea
              {...field}
              {...mentionsConfig}
              clearContent={clearContent}
              trackingLabel="To Do Comments"
              id="to-do-comment"
              placeholder="Your message is here..."
              actions={['dictation']}
              classes={{ textarea: 'pb-[60px] max-w-[592px]' }}
              onEditorChange={handleEditorChange}
            />
          )
        }
        />
        <Button
          variant="contained"
          className={clsx(
            '!absolute !bottom-4 !right-8 !pointer-events-auto',
            { '!cursor-not-allowed': !isValid || isSubmitting || isCommenting },
          )}
          color="primary"
          type="submit"
          onClick={() => {}}
          disabled={!isValid || isSubmitting || isCommenting}
        >
          {isSubmitting || isCommenting ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </form>
  );
}

export default function Comments({ toDoId, isAbandoned = false, hasComments = false }: Props) {
  const { data, isLoading, refetch } = useToDoComments(toDoId, hasComments);
  const comments = data ?? [];

  const rows = useMemo<RowProps[]>(() => (
    comments.reduce((
      agg: RowProps[],
      comment,
    ) => {
      agg.push({ data: comment });
      return agg;
    }, [])
  ), [comments]);

  const { requestMedia } = useMedia();

  useEffect(() => {
    const userIds = compact(rows.map((item) => item.data?.creator?.id));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [rows, requestMedia]);

  useSignalREffect('OnToDoCommentAdded', () => {
    refetch?.();
  });

  return (
    <div className="flex flex-auto w-full h-full flex-col overflow-hidden">
      {isLoading || (rows.length === 0 && isAbandoned)
        ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <EmptyState
              isLoading={isLoading}
              loadingText="Checking for the comments..."
            >
              {isAbandoned ? (
                <span className="font-semibold">
                  No Comments Found.
                </span>
              ) : ''}
            </EmptyState>
          </div>
        ) : (
          <>
            <div className="w-full px-6 pt-3">
              {(rows.length > 0 || !isAbandoned) && (
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Comments
                </h2>
              )}
            </div>
            {rows.length > 0 && (
              <div className="flex-1 overflow-y-auto min-h-0 px-6">
                <VirtualList<RowProps>
                  renderItemComponent={(props: RowProps) => <CommentRow {...props} />}
                  data={rows}
                  className="-mx-4 mb-3 auto-scrollable"
                  scrollContainerClassName="px-4 !overflow-y-hidden"
                />
              </div>
            )}
          </>
        )}
      {!isAbandoned && (
        <CommentForm toDoId={toDoId} />
      )}
    </div>
  );
}
